import type {ScanContext} from "../scan-model/ScanContext.tsx";
import {scanVersions} from "./2-ScanVersions.tsx";
import {scanVersionsJobs} from "./3-ScanJobs.ts";
import {scanSharedJobs} from "./1-ScanSharedJobs.tsx";
import {mergeResults} from "./5-MergeResults.tsx";
import {scanTaskHistory} from "./4-ScanTaskHistory.tsx";
import {scanDrivers} from "./1-ScanDrivers.tsx";
import {scanEnvironments} from "./3-ScanEnvironments.ts";
import {scanSchedules} from "./3-ScanSchedules.tsx";
import {scanSupportInfo} from "./1-ScanSupportInfo.tsx";
import {optionalScans} from "./3-OptionalScans.tsx";


/**
 * Run an optional scan phase. If it throws, log the failure and return the
 * fallback so the rest of the scan can continue. Phases that aren't required
 * to produce a meaningful overall result use this so a single-phase failure
 * (e.g. a flaky network request to one METL endpoint) doesn't kill the whole
 * scan.
 */
async function optional<T>(ctx: ScanContext, phase: string, fn: () => Promise<T>, fallback: T): Promise<T> {
    try {
        return await fn()
    } catch (e) {
        ctx.logError(`Optional scan phase "${phase}" failed and will be skipped. The rest of the scan will continue.`, e)
        return fallback
    }
}

export async function performScan(ctx: ScanContext) {
    try {
        ctx.log("Starting scan")
        const supportInfo = await optional(ctx, "support info", () => scanSupportInfo(ctx), {})
        const sharedJobs = await optional(ctx, "shared jobs", () => scanSharedJobs(ctx), {list: []})
        const driverReport = await optional(ctx, "drivers", () => scanDrivers(ctx), undefined)
        // Versions are required — without them there is nothing to scan.
        const versionsScanPlan = await scanVersions(ctx)
        const optionalInfo = await optional(ctx, "optional scans", () => optionalScans(ctx, versionsScanPlan), {})
        const scannedEnvs = await optional(ctx, "environments", () => scanEnvironments(ctx, versionsScanPlan.versions), undefined)
        // Jobs are required — task history and merging both depend on them.
        const scannedJobs = await scanVersionsJobs(ctx, versionsScanPlan.versions)
        const scannedSchedules = await optional(ctx, "schedules", () => scanSchedules(ctx, versionsScanPlan), undefined)
        const scannedTaskHistory = await optional(ctx, "task history", () => scanTaskHistory(ctx, versionsScanPlan, scannedJobs), undefined)
        const mergedScanResult = mergeResults(ctx, scannedJobs, scannedTaskHistory, driverReport, scannedEnvs, sharedJobs, supportInfo, optionalInfo)
        mergedScanResult.scheduleScan = scannedSchedules
        return mergedScanResult
    } catch(e) {
        ctx.logError("An error occurred during a required scan phase which has prevented continuing.", e)
        throw e
    }
}